import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useProviders, ProviderWithProducts } from "../../hooks/useProviders";
import { useApp } from "../../contexts/AppContext";

const getAvatar = (p: ProviderWithProducts) =>
  p.avatar_url ||
  `https://public.readdy.ai/ai/img_res/0d767a7de7d80c84bee47e2d38796ead.jpg${p.id.slice(0, 8)}&orientation=squarish`;

const getCover = (p: ProviderWithProducts) =>
  p.cover_image_url ||
  `https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20workshop%20artisan%20tools%20elegant%20dark%20moody%20atmosphere%20professional%20studio&width=480&height=280&seq=prov-cv-${p.id.slice(0, 8)}&orientation=landscape`;

export default function ProvidersPage() {
  const [searchParams] = useSearchParams();
  const { providers, loading } = useProviders();
  const { toggleFollow, followedProviders, isLoggedIn } = useApp();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const [minRating, setMinRating] = useState(0);
  const [planFilter, setPlanFilter] = useState<"all" | "free" | "light">("all");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // カテゴリをURL paramsから読み込む
  const [selectedCat, setSelectedCat] = useState(() => {
    return searchParams.get("category") || "すべて";
  });

  // URL paramsが変わったらカテゴリを更新
  useEffect(() => {
    const catParam = searchParams.get("category");
    if (catParam) setSelectedCat(catParam);
  }, [searchParams]);

  const cats = useMemo(() => {
    const names = providers.map((p) => p.category).filter(Boolean) as string[];
    return ["すべて", ...Array.from(new Set(names))];
  }, [providers]);

  const filtered = useMemo(() => {
    return providers
      .filter((p) => {
        const matchSearch =
          !search ||
          (p.service_name || "").includes(search) ||
          p.name.includes(search) ||
          (p.skills || []).some((s) => s.includes(search));
        const matchCat = selectedCat === "すべて" || p.category === selectedCat;
        const matchRating = (p.rating ?? 0) >= minRating;
        const matchPlan = planFilter === "all" || p.plan === planFilter;
        return matchSearch && matchCat && matchRating && matchPlan;
      })
      .sort((a, b) =>
        sort === "popular"
          ? (b.follower_count ?? 0) - (a.follower_count ?? 0)
          : sort === "rating"
          ? (b.rating ?? 0) - (a.rating ?? 0)
          : (b.review_count ?? 0) - (a.review_count ?? 0)
      );
  }, [providers, search, selectedCat, sort, minRating, planFilter]);

  const hasActiveFilters = selectedCat !== "すべて" || minRating > 0 || planFilter !== "all";

  const SidebarContent = () => (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-sans font-medium text-charcoal/60 mb-2 uppercase tracking-wider">キーワード検索</p>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm" />
          <input
            type="text"
            placeholder="事業者名・技術..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 bg-stone-50 rounded-xl text-sm font-sans text-charcoal outline-none border border-stone-200 focus:border-teal transition-colors"
          />
        </div>
      </div>
      <div>
        <p className="text-xs font-sans font-medium text-charcoal/60 mb-2 uppercase tracking-wider">並び替え</p>
        <div className="space-y-1">
          {[
            { v: "popular", l: "フォロワー数順" },
            { v: "rating", l: "評価の高い順" },
            { v: "reviews", l: "レビュー数順" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setSort(s.v)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-sans transition-colors cursor-pointer ${sort === s.v ? "bg-deep-green text-white" : "text-charcoal/60 hover:bg-stone-100"}`}
            >
              {sort === s.v && <i className="ri-check-line mr-2" />}{s.l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-sans font-medium text-charcoal/60 mb-2 uppercase tracking-wider">カテゴリ</p>
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setSelectedCat(c)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-sans transition-colors cursor-pointer ${selectedCat === c ? "bg-teal/10 text-teal" : "text-charcoal/60 hover:bg-stone-100"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-sans font-medium text-charcoal/60 mb-2 uppercase tracking-wider">最低評価</p>
        <div className="space-y-1">
          {[
            { v: 0, l: "すべて" },
            { v: 4.0, l: "★4.0以上" },
            { v: 4.5, l: "★4.5以上" },
            { v: 4.8, l: "★4.8以上" },
          ].map((r) => (
            <button
              key={r.v}
              onClick={() => setMinRating(r.v)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-sans transition-colors cursor-pointer ${minRating === r.v ? "bg-gold/10 text-gold" : "text-charcoal/60 hover:bg-stone-100"}`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-sans font-medium text-charcoal/60 mb-2 uppercase tracking-wider">プラン</p>
        <div className="space-y-1">
          {[
            { v: "all" as const, l: "すべて" },
            { v: "light" as const, l: "ライトプランのみ" },
            { v: "free" as const, l: "フリープランのみ" },
          ].map((p) => (
            <button
              key={p.v}
              onClick={() => setPlanFilter(p.v)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-sans transition-colors cursor-pointer ${planFilter === p.v ? "bg-deep-green/10 text-deep-green" : "text-charcoal/60 hover:bg-stone-100"}`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>
      {hasActiveFilters && (
        <button
          onClick={() => { setSelectedCat("すべて"); setMinRating(0); setPlanFilter("all"); setSearch(""); }}
          className="w-full py-2.5 rounded-xl border border-stone-200 text-charcoal/50 text-xs font-sans cursor-pointer hover:border-teal hover:text-teal transition-colors whitespace-nowrap"
        >
          <i className="ri-filter-off-line mr-1" />フィルターをリセット
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        <section className="bg-deep-green py-14 px-4">
          <div className="max-w-screen-xl mx-auto">
            <p className="font-display italic text-gold text-sm tracking-widest mb-3">Find Artisans</p>
            <h1 className="font-serif text-white text-4xl md:text-5xl font-light mb-4">事業者一覧</h1>
            <p className="text-white/60 text-sm font-sans mb-6">
              職人・専門家を探して、無料サンプルで技術を確認しましょう。
            </p>
          </div>
        </section>

        <div className="max-w-screen-xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-7">
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl p-5 border border-stone-100 sticky top-24">
                <div className="flex items-center justify-between mb-5">
                  <p className="font-serif text-deep-green text-sm">絞り込み・検索</p>
                  {hasActiveFilters && (
                    <span className="text-xs bg-teal text-white px-2 py-0.5 rounded-full font-sans">適用中</span>
                  )}
                </div>
                <SidebarContent />
              </div>
            </aside>

            <div className="lg:hidden flex gap-3 mb-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-full text-sm font-sans border border-stone-200 cursor-pointer hover:border-teal transition-colors whitespace-nowrap"
              >
                <i className="ri-filter-3-line" />絞り込み
                {hasActiveFilters && <span className="bg-teal text-white text-xs px-1.5 rounded-full">!</span>}
              </button>
            </div>

            {sidebarOpen && (
              <div className="fixed inset-0 z-50 lg:hidden">
                <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
                <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-5 overflow-y-auto">
                  <div className="flex items-center justify-between mb-5">
                    <p className="font-serif text-deep-green">絞り込み・検索</p>
                    <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer text-charcoal/50">
                      <i className="ri-close-line text-xl" />
                    </button>
                  </div>
                  <SidebarContent />
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="w-full mt-5 bg-deep-green text-white py-3 rounded-full text-sm font-sans cursor-pointer whitespace-nowrap"
                  >
                    {filtered.length}件を表示
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <p className="text-charcoal/50 text-xs font-sans">{loading ? "読み込み中..." : `${filtered.length}件の事業者`}</p>
                <div className="hidden lg:flex gap-2">
                  {[
                    { v: "popular", l: "フォロワー順" },
                    { v: "rating", l: "評価順" },
                    { v: "reviews", l: "レビュー数順" },
                  ].map((s) => (
                    <button
                      key={s.v}
                      onClick={() => setSort(s.v)}
                      className={`px-3 py-1.5 rounded-full text-xs font-sans whitespace-nowrap cursor-pointer border transition-all ${sort === s.v ? "bg-deep-green text-white border-deep-green" : "bg-white text-charcoal border-stone-200 hover:border-teal"}`}
                    >
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden border border-stone-100 animate-pulse">
                      <div className="h-52 bg-stone-200" />
                      <div className="p-5 space-y-3">
                        <div className="h-4 bg-stone-200 rounded w-3/4" />
                        <div className="h-3 bg-stone-200 rounded w-1/2" />
                        <div className="h-8 bg-stone-200 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-24 bg-white rounded-2xl">
                  <i className="ri-store-2-line text-5xl text-charcoal/20 block mb-4" />
                  <p className="text-charcoal/40 font-sans text-sm">
                    {providers.length === 0
                      ? "まだ事業者が登録されていません。最初の事業者になりましょう！"
                      : "条件に合う事業者が見つかりませんでした"}
                  </p>
                  {providers.length === 0 ? (
                    <Link to="/register" className="mt-4 inline-block bg-deep-green text-white px-6 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                      事業者として登録する
                    </Link>
                  ) : (
                    <button
                      onClick={() => { setSearch(""); setSelectedCat("すべて"); setMinRating(0); setPlanFilter("all"); }}
                      className="mt-4 text-teal text-sm font-sans cursor-pointer hover:underline"
                    >
                      条件をリセット
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {filtered.map((p, i) => {
                    const isFollowing = followedProviders.has(p.id);
                    const avatar = getAvatar(p);
                    const cover = getCover(p);
                    return (
                      <div
                        key={p.id}
                        className="bg-white rounded-2xl overflow-hidden border border-stone-100 transition-all duration-300 hover:-translate-y-1"
                        style={{ animationDelay: `${i * 0.06}s` }}
                      >
                        <div className="relative h-52 overflow-hidden group">
                          <img
                            src={cover}
                            alt={p.service_name ?? p.name}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-deep-green/65 to-transparent" />
                          <div className="absolute bottom-3 left-4 flex items-end gap-3">
                            <img
                              src={avatar}
                              alt={p.name}
                              className="w-14 h-14 rounded-full border-2 border-white object-cover flex-shrink-0"
                            />
                            <div>
                              <p className="text-white font-serif text-base leading-tight">{p.service_name || p.name}</p>
                              <p className="text-white/70 text-xs font-sans">{p.name} · {p.location || "日本"}</p>
                            </div>
                          </div>
                          <div className="absolute top-3 right-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${p.plan === "light" ? "bg-gold text-white" : "bg-white/80 text-charcoal"}`}>
                              {p.plan === "light" ? "ライト" : "フリー"}
                            </span>
                          </div>
                        </div>

                        <div className="p-5">
                          {p.slogan && (
                            <p className="font-display italic text-teal text-sm mb-2">"{p.slogan}"</p>
                          )}
                          {p.bio && (
                            <p className="text-charcoal/55 text-xs font-sans leading-relaxed mb-3 line-clamp-2">{p.bio}</p>
                          )}
                          {(p.skills && p.skills.length > 0) && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {p.skills.slice(0, 3).map((s) => (
                                <span key={s} className="tag-pill">{s}</span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-xs text-charcoal/50 font-sans mb-3">
                            <span>
                              <span className="star-gold">★</span>{" "}
                              <strong className="text-charcoal">{(p.rating ?? 0).toFixed(1)}</strong>{" "}
                              ({p.review_count ?? 0})
                            </span>
                            <span><i className="ri-user-follow-line" /> {(p.follower_count ?? 0).toLocaleString()}</span>
                            <span><i className="ri-play-circle-line" /> {p.products.length}本</span>
                          </div>
                          {p.products.length > 0 && (
                            <div className="flex gap-1.5 mb-4">
                              {p.products.slice(0, 3).map((prod) => (
                                <div key={prod.id} className="flex-1 h-12 rounded-lg overflow-hidden relative">
                                  <img
                                    src={prod.thumbnail_url || `https://readdy.ai/api/search-image?query=craft%20workshop%20tools%20artisan%20professional%20minimal&width=160&height=90&seq=thumb-${prod.id.slice(0,8)}&orientation=landscape`}
                                    alt={prod.title}
                                    className="w-full h-full object-cover object-top"
                                  />
                                  <div className="absolute inset-0 bg-deep-green/20" />
                                  <span className="absolute bottom-0.5 left-1 text-white text-xs bg-deep-green/70 px-1 rounded">
                                    {prod.type === "video" ? "動画" : "教材"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => isLoggedIn && toggleFollow(p.id)}
                              className={`flex-1 py-2.5 rounded-full text-xs font-sans font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${isFollowing ? "bg-teal text-white" : "border border-teal text-teal hover:bg-teal hover:text-white"}`}
                            >
                              {isFollowing ? "✓ フォロー中" : "+ フォロー"}
                            </button>
                            <Link
                              to={`/providers/${p.id}`}
                              className="flex-1 py-2.5 rounded-full text-xs font-sans font-medium whitespace-nowrap cursor-pointer bg-deep-green text-white text-center hover:bg-teal transition-colors"
                            >
                              詳細を見る →
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
